/**
 * SectionHeader — Klickbare Sektion-Ueberschrift mit Collapse-Toggle.
 *
 * Pure presentational. State-Management liegt im Parent (SectionList → MainPage).
 */

import { ChevronDown, ChevronUp, Loader2, X } from 'lucide-react';
import type { SectionId } from '../lib/sectionQueryBuilder';
import { SECTION_COLORS, SECTION_LABELS } from '../lib/dashboardStatsMapper';

export interface SectionHeaderProps {
  sectionId: SectionId;
  total: number;
  loaded: number | null;
  collapsed: boolean;
  loading: boolean;
  subFilterChip?: { label: string; onClear: () => void } | null;
  onToggle: () => void;
}

export default function SectionHeader({
  sectionId,
  total,
  loaded,
  collapsed,
  loading,
  subFilterChip,
  onToggle,
}: SectionHeaderProps) {
  const color = SECTION_COLORS[sectionId];
  const title = SECTION_LABELS[sectionId];

  return (
    <div className="flex items-center justify-between border border-[#DAD9D9] rounded-lg bg-white px-3 py-2">
      <button
        type="button"
        onClick={onToggle}
        className="flex items-center gap-2 text-left flex-1"
        aria-expanded={!collapsed}
      >
        <span className="inline-block w-3 h-3 rounded-sm flex-shrink-0" style={{ background: color }} />
        <span className="text-sm font-semibold text-[#1A1A1A]">{title}</span>
        <span className="text-xs text-gray-500">
          {total.toLocaleString('de-DE')}
          {loaded != null && loaded !== total && (
            <span className="ml-1.5 text-gray-400">· zeige {loaded}</span>
          )}
        </span>
        {loading && <Loader2 className="w-3 h-3 animate-spin text-gray-400" />}
      </button>
      <div className="flex items-center gap-2">
        {subFilterChip && (
          <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded bg-[#E9FF57] text-[#1A1A1A] font-medium">
            {subFilterChip.label}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                subFilterChip.onClear();
              }}
              aria-label="Filter loeschen"
              className="hover:bg-black/10 rounded p-0.5"
            >
              <X className="w-3 h-3" />
            </button>
          </span>
        )}
        <button
          type="button"
          onClick={onToggle}
          className="p-1 rounded hover:bg-gray-50"
          aria-label={collapsed ? 'Sektion ausklappen' : 'Sektion einklappen'}
        >
          {collapsed
            ? <ChevronDown className="w-4 h-4 text-[#1A1A1A]" />
            : <ChevronUp className="w-4 h-4 text-[#1A1A1A]" />}
        </button>
      </div>
    </div>
  );
}
